import { Box, Divider, HStack, IconButton, VStack } from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabaseMutationFn, useSupabaseTypedClient } from 'hooks/reactQuery';
import { extractCodeFromStorage } from 'hooks/useCodeStorage';
import { useExitAlert } from 'hooks/useExitAlert';
import { useToastWrapper } from 'hooks/useToastWrapper';
import html2canvas from 'html2canvas';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useCurrentUser } from 'auth/CurrentUserProvider';
import { BigBadge } from 'components/BigBadge';
import CommentButton from 'components/CommentButton';
import DevSandbox from 'components/DevSandbox';
import DevSandboxTooltip from 'components/DevSandbox/DevSandboxTooltip';
import EditorSettingsButton, { ShareButton } from 'components/DevSandbox/EditorSettingsButton';
import { MerlinToggleButton } from 'components/DevSandbox/Merlin';
import BrowserTab from 'components/DevSandbox/PaneTabs/BrowserTab';
import DiscussionTab from 'components/DevSandbox/PaneTabs/Discussion/DiscussionTab';
import RightSideActions from 'components/DevSandbox/RightSideActions';
import { CODE_FREE_LIMIT, CODE_PAID_LIMIT } from 'components/DevSandbox/constants';
import { isCodeSame } from 'components/DevSandbox/utils';
import Icon from 'components/Icon';
import LikeButton from 'components/LikeButton';
import Link from 'components/Link';
import { ProtectedElement } from 'components/ProtectedElement';
import RenameCodeInput from 'components/RenameCodeInput';
import Text from 'components/Text';
import ToggleCodePrivacyLock from 'components/ToggleCodePrivacyLock';
import UserAvatar from 'components/UserAvatar';
import { useMediaQueries } from 'layout/MediaQueriesProvider';
import { Support_Type_Enum } from 'types/databaseEnums';
import {
  EdgeFunctionName,
  CodeFrame,
  SubmitCodeResponse,
  SubmitCodeParams,
} from 'types/edgeFunctions';
import { ERoute, TanstackQueryName } from 'types/frontend';

import SolutionsTab from './SolutionsTab';

interface ICodeFramePlaygroundProps {
  codeFrame: CodeFrame;
}

const CodeFramePlayground = ({
  codeFrame: {
    id,
    title,
    user,
    html,
    css,
    js,
    is_liked,
    is_private,
    likes,
    comments_count,
    solution_url,
    solutions_count,
    is_my_solution,
    screenshot_url,
    editor_settings,
  },
}: ICodeFramePlaygroundProps) => {
  const [localTitle, setLocalTitle] = useState<string>(title);
  const [codeFrameUrl, setCodeFrameUrl] = useState<string>('');
  const { id: currentUserId, screenName: currentUserScreenName, isFree } = useCurrentUser();
  const [searchParams] = useSearchParams();

  const { isSmallScreen } = useMediaQueries();

  const navigate = useNavigate();

  const mutation = useMutation<SubmitCodeResponse, string, SubmitCodeParams>({
    mutationFn: supabaseMutationFn((params) =>
      supabase.functions.invoke<SubmitCodeResponse>(EdgeFunctionName.SubmitCode, {
        body: params as SubmitCodeParams,
      })
    ),
    onError: () => {
      toastError('Could not save code, try again later.');
    },
  });

  const handleSave = async (skipScreenshot: boolean) => {
    const CODE_LIMIT = isFree ? CODE_FREE_LIMIT : CODE_PAID_LIMIT;

    const storedHtml = extractCodeFromStorage({
      type: 'code-frame',
      screenName: id,
      userId: currentUserId,
      code: 'html',
    });

    const storedCss = extractCodeFromStorage({
      type: 'code-frame',
      screenName: id,
      userId: currentUserId,
      code: 'css',
    });

    const storedJs = extractCodeFromStorage({
      type: 'code-frame',
      screenName: id,
      userId: currentUserId,
      code: 'js',
    });

    if (storedHtml.length > CODE_LIMIT) {
      return toastError(`Exceeded maximum ${CODE_LIMIT} characters of HTML`);
    }

    if (storedCss.length > CODE_LIMIT) {
      return toastError(`Exceeded maximum ${CODE_LIMIT} characters of CSS`);
    }

    if (storedJs.length > CODE_LIMIT) {
      return toastError(`Exceeded maximum ${CODE_LIMIT} characters of JS`);
    }

    let iframeScreenshot = '';

    if (!skipScreenshot) {
      iframeScreenshot = await createIframeScreenshot();
    }

    // Include editor settings so viewers can see the solution with the author's config
    const storedEditorSettings = (() => {
      try {
        const raw = localStorage.getItem('bds-editor-settings');
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        const { framework, cssMode, packages } = parsed;
        return { framework, cssMode, packages };
      } catch {
        return null;
      }
    })();

    mutation.mutate(
      {
        type: 'code-frame',
        entityId: id,
        entityScreenName: '',
        title,
        is_private,
        html: storedHtml,
        css: storedCss,
        js: storedJs,
        iframe_screenshot: iframeScreenshot,
        editor_settings: storedEditorSettings,
      },
      {
        onSuccess: (data) => {
          queryClient.refetchQueries({
            queryKey: [TanstackQueryName.GetCodeFrame, solution_url],
          });
          queryClient.refetchQueries({
            queryKey: [TanstackQueryName.GetCodeFrame, data.solution_url],
          });
          queryClient.refetchQueries({
            queryKey: [TanstackQueryName.GetCodeFrames, currentUserId],
          });
          queryClient.refetchQueries({ queryKey: [TanstackQueryName.GetCodeFrames, null] });

          if (!skipScreenshot) {
            toastSuccess('Code saved');
          }
        },
      }
    );
  };

  useEffect(() => {
    return () => {
      const storedHtml = extractCodeFromStorage({
        type: 'code-frame',
        screenName: id,
        userId: currentUserId,
        code: 'html',
      });

      const storedCss = extractCodeFromStorage({
        type: 'code-frame',
        screenName: id,
        userId: currentUserId,
        code: 'css',
      });

      const storedJs = extractCodeFromStorage({
        type: 'code-frame',
        screenName: id,
        userId: currentUserId,
        code: 'js',
      });

      const isCodeChanged = isCodeSame({ html, css, js, storedHtml, storedCss, storedJs });

      if (!isCodeChanged) {
        return;
      }

      handleSave(true);
    };
  }, []);

  const storedHtml = extractCodeFromStorage({
    type: 'code-frame',
    screenName: id,
    userId: currentUserId,
    code: 'html',
  });

  const storedCss = extractCodeFromStorage({
    type: 'code-frame',
    screenName: id,
    userId: currentUserId,
    code: 'css',
  });

  const storedJs = extractCodeFromStorage({
    type: 'code-frame',
    screenName: id,
    userId: currentUserId,
    code: 'js',
  });

  const isCodeChanged = isCodeSame({ html, css, js, storedHtml, storedCss, storedJs });

  // Cmd+Shift+S: trigger DB save for owners (local backup handled by useCodeVersionHistory)
  useEffect(() => {
    if (!is_my_solution) return;

    const handler = () => {
      handleSave(true);
    };
    window.addEventListener('bds:save-shortcut', handler);
    return () => window.removeEventListener('bds:save-shortcut', handler);
  }, [is_my_solution]);

  useExitAlert({
    shouldShowBrowserExitAlert: isCodeChanged,
  });

  const createIframeScreenshot = async (): Promise<string> => {
    const iframe = document.querySelector('.bds-code-frame');
    // @ts-ignore
    const iframeDocument = iframe.contentDocument || iframe.contentWindow.document;

    try {
      const canvas = await html2canvas(iframeDocument.body, {
        useCORS: true,
        allowTaint: true,
        width: 1440,
        height: 1080,
      });

      return canvas.toDataURL('image/png');
    } catch (err) {
      toastError(`Error capturing screenshot ${err}`);
    }

    return '';
  };

  const codeEditorRef = useRef<any>(null);

  const supabase = useSupabaseTypedClient();
  const queryClient = useQueryClient();

  const { toastSuccess, toastError } = useToastWrapper();

  const hasTabInUrl =
    location.pathname.includes('browser') ||
    location.pathname.includes('comments') ||
    location.pathname.includes('explore');
  const tabScreenName = location.pathname.split('/').pop();
  const browserUrl = hasTabInUrl
    ? window.location.href.replace(`/${tabScreenName}`, '')
    : window.location.href;

  const searchParamsMode = searchParams.get('mode');
  const mode = searchParamsMode === 'full-page' ? 'full-page' : 'interactive';

  const accountDeleted = user.id === '' || user.id === null;

  return (
    <>
      <DevSandbox
        ref={codeEditorRef}
        id={`code-frame-${id}-${currentUserId}`}
        browserUrl={browserUrl}
        title={title}
        mode={mode}
        entityType="code-frame"
        entityId={id}
        initialLayout={is_my_solution ? undefined : 'preview-only'}
        authorEditorSettings={!is_my_solution ? editor_settings : undefined}
        merlinConfig={is_my_solution ? { entityName: title, entityType: 'codeframe' } : undefined}
        navigation={{
          leftSide: (
            <HStack pl={1}>
              <VStack spacing={0} alignItems="flex-start">
                <RenameCodeInput
                  title={title}
                  onTitleChange={(title) => setLocalTitle(title)}
                  isMySolution={is_my_solution}
                  type="code-frame"
                  shortTitle
                  renameCodeParams={{
                    entityId: id,
                    userScreenName: currentUserScreenName,
                    queryKey: [TanstackQueryName.GetCodeFrame, solution_url],
                    repositoryUrl: '',
                    previewUrl: '',
                    invalidateKeys: [
                      [TanstackQueryName.GetCodeFrames, null],
                      [TanstackQueryName.GetCodeFrames, currentUserId],
                      [TanstackQueryName.GetProfileCode, user.id, 'code-frame'],
                      [TanstackQueryName.GetUserCode, currentUserId, 0],
                      [TanstackQueryName.GetUserCode, currentUserId, 1],
                      [TanstackQueryName.GetUserCode, currentUserId, 2],
                      [TanstackQueryName.GetCodeFrame, solution_url],
                    ],
                  }}
                />
                <HStack
                  spacing={4}
                  width="fit-content"
                  cursor={accountDeleted ? 'default' : 'pointer'}
                  alignItems="flex-start"
                >
                  <HStack>
                    {isSmallScreen ? (
                      <UserAvatar
                        key={user.id}
                        name={user.name}
                        src={user.avatar_url}
                        onClick={(e) => {
                          if (accountDeleted) {
                            return;
                          }

                          navigate(`${ERoute.Profile}/${user.screen_name}`);
                        }}
                        experience={user.xp}
                        size="xs"
                        hideCircularProgress
                      />
                    ) : (
                      <>
                        {!accountDeleted ? (
                          <Link to={`${ERoute.Profile}/${user.screen_name}`} color="white">
                            <Text fontSize="micro" maxW="200px" isTruncated opacity="secondary">
                              {user.name}
                            </Text>
                          </Link>
                        ) : (
                          <Text fontSize="micro" maxW="200px" isTruncated opacity="secondary">
                            {user.name}
                          </Text>
                        )}
                      </>
                    )}
                    <BigBadge plan={user.plan} size="small" />
                  </HStack>
                </HStack>
              </VStack>
              <Divider height="24px" orientation="vertical" />
              <HStack spacing={4}>
                <LikeButton
                  userId={user.id}
                  likes={likes}
                  likeCodeParams={{
                    entityId: id,
                    isLiked: is_liked,
                    queryKey: [TanstackQueryName.GetCodeFrame, solution_url],
                    invalidateKeys: [
                      [TanstackQueryName.GetCodeFrames, null],
                      [TanstackQueryName.GetCodeFrames, currentUserId],
                      [TanstackQueryName.GetProfileCode, user.id, 'code-frame'],
                      [TanstackQueryName.GetUserCode, currentUserId, 0],
                      [TanstackQueryName.GetUserCode, currentUserId, 1],
                      [TanstackQueryName.GetUserCode, currentUserId, 2],
                    ],
                  }}
                />
                {!isSmallScreen && (
                  <CommentButton comments={comments_count} url={`${solution_url}/comments`} />
                )}
              </HStack>
            </HStack>
          ),
          header: (
            <HStack spacing={2}>
              {is_my_solution && <MerlinToggleButton />}
              {is_my_solution ? (
                <Box data-tour="save-button" display="inline-flex">
                  <ProtectedElement
                    scheme="outline"
                    onClick={async () => await handleSave(false)}
                    isLoading={mutation.isPending}
                    leftIcon={<Icon type="CodeSave" />}
                  >
                    Save
                  </ProtectedElement>
                </Box>
              ) : (
                <ProtectedElement asButton={false}>
                  <DevSandboxTooltip label="Fork code">
                    <IconButton
                      aria-label="fork solution"
                      onClick={() => {
                        navigate(`${ERoute.CodeFrames}${ERoute.NewCodeFrame}`, {
                          state: {
                            html,
                            css,
                            js,
                            screenshotUrl: screenshot_url,
                            editorSettings: editor_settings,
                            shouldCreate: true,
                            isFork: true,
                          },
                        });
                      }}
                    >
                      <Icon type="ForkCode" />
                    </IconButton>
                  </DevSandboxTooltip>
                </ProtectedElement>
              )}
            </HStack>
          ),
          rightSide: (
            <RightSideActions
              leftActions={
                !isSmallScreen ? (
                  <HStack spacing={2}>
                    {is_my_solution && <EditorSettingsButton />}
                    {is_my_solution && (
                      <ToggleCodePrivacyLock
                        type="code-frame"
                        togglePrivacyCodeParams={{
                          isPrivate: is_private,
                          entityId: id,
                          queryKey: [TanstackQueryName.GetCodeFrame, solution_url],
                          invalidateKeys: [
                            [TanstackQueryName.GetCodeFrames, null],
                            [TanstackQueryName.GetCodeFrames, currentUserId],
                            [TanstackQueryName.GetProfileCode, user.id, 'code-frame'],
                            [TanstackQueryName.GetUserCode, currentUserId, 0],
                            [TanstackQueryName.GetUserCode, currentUserId, 1],
                            [TanstackQueryName.GetUserCode, currentUserId, 2],
                          ],
                        }}
                      />
                    )}
                    <ShareButton />
                  </HStack>
                ) : undefined
              }
            />
          ),
        }}
        codeTabs={{
          html,
          css,
          js,
        }}
        callbacks={{
          onCodeFrameUrlChange: (codeFrameUrl) => {
            setCodeFrameUrl(codeFrameUrl);
          },
        }}
        flags={{
          loadCodeFromServer: is_my_solution ? false : true,
        }}
        paneTabs={{
          tabs: [
            {
              screenName: 'browser',
              title: 'Browser',
              icon: <Icon type="Browser" size={16} />,
              content: (
                <BrowserTab
                  browserUrl={browserUrl}
                  codeFrameUrl={codeFrameUrl}
                  onCodeFrameUrlChange={codeEditorRef.current?.onCodeFrameUrlChange}
                />
              ),
            },
            {
              title: `${comments_count > 0 ? `Comments (${comments_count})` : 'Comments'}`,
              screenName: 'comments',
              icon: <Icon type="CommunityQuestions" />,
              content: (
                <DiscussionTab
                  entityId={id}
                  type={Support_Type_Enum.Feedback}
                  isMySolution={is_my_solution}
                  refetch={() => {
                    queryClient.invalidateQueries({
                      queryKey: [TanstackQueryName.GetCodeFrames, currentUserId],
                    });
                    queryClient.invalidateQueries({
                      queryKey: [TanstackQueryName.GetCodeFrames, null],
                    });
                    queryClient.invalidateQueries({
                      queryKey: [TanstackQueryName.GetCodeFrame, solution_url],
                    });
                  }}
                />
              ),
            },
            {
              screenName: 'explore',
              title: `${solutions_count > 0 ? `Explore (${solutions_count})` : 'Explore'}`,
              icon: <Icon type="SolutionsTab" size={16} />,
              content: <SolutionsTab />,
            },
          ],
        }}
        footer={{
          hideTools: !is_my_solution,
          assetsConfig: is_my_solution ? {
            showDesignTab: false,
            showYourFilesTab: true,
            showPackagesTab: true,
            showToolsTab: false,
            entityType: 'code-frame',
            entityId: id,
          } : null,
        }}
      />
    </>
  );
};

export default CodeFramePlayground;
